import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";

const ProgressBar = () => {
  const [animatingIndeterminate, setAnimatingIndeterminate] = useState(true);
  const [animatingStriped, setAnimatingStriped] = useState(true);

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* SECTION 1: BASIC PROGRESS BARS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Basic Progress Bars</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* 1: 25% Progress */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">25% Progress</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{width: "25%"}}></div>
            </div>
            <CopyCode number_code={1} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-amber-500 rounded-full\" style={{'width': '25%'}}></div>\n</div>"} />
          </div>

          {/* 2: 50% Progress */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">50% Progress</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{width: "50%"}}></div>
            </div>
            <CopyCode number_code={2} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-blue-500 rounded-full\" style={{'width': '50%'}}></div>\n</div>"} />
          </div>

          {/* 3: 75% Progress */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">75% Progress</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{width: "75%"}}></div>
            </div>
            <CopyCode number_code={3} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-amber-500 rounded-full\" style={{'width': '75%'}}></div>\n</div>"} />
          </div>

          {/* 4: 100% Complete */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">100% Complete</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full" style={{width: "100%"}}></div>
            </div>
            <CopyCode number_code={4} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-green-500 rounded-full\" style={{'width': '100%'}}></div>\n</div>"} />
          </div>

          {/* 5: Indeterminate */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Indeterminate</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-blue-500 to-transparent" style={{animation: "slide 1.5s infinite"}}></div>
            </div>
            <CopyCode number_code={5} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full w-1/3 bg-gradient-to-r from-transparent via-blue-500\" style={{'animation': 'slide 1.5s infinite'}}></div>\n</div>"} />
          </div>

          {/* 6: With Label */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Label</label>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">Upload</span>
              <span className="text-slate-300">65%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full" style={{width: "65%"}}></div>
            </div>
            <CopyCode number_code={6} code={"<div className=\"flex justify-between text-xs mb-1\">\n  <span className=\"text-slate-400\">Upload</span>\n  <span className=\"text-slate-300\">65%</span>\n</div>\n<div className=\"h-2 bg-slate-700 rounded-full\">\n  <div className=\"h-full bg-orange-500\" style={{'width': '65%'}}></div>\n</div>"} />
          </div>
        </div>
      </div>

      {/* SECTION 2: STYLED PROGRESS BARS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Styled Progress Bars</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* 7: Thick Bar */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Thick Bar</label>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-blue-500 rounded-full" style={{width: "60%"}}></div>
            </div>
            <CopyCode number_code={7} code={"<div className=\"h-4 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-gradient-to-r from-amber-500 to-blue-500\" style={{'width': '60%'}}></div>\n</div>"} />
          </div>

          {/* 8: Rainbow Gradient */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Rainbow Gradient</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full" style={{width: "80%"}}></div>
            </div>
            <CopyCode number_code={8} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500\" style={{'width': '80%'}}></div>\n</div>"} />
          </div>

          {/* 9: Striped Bar */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Striped Bar (Animated)</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{width: "45%", backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,.1) 0px, rgba(255,255,255,.1) 10px, transparent 10px, transparent 20px)", animation: animatingStriped ? "striped-animation 1s linear infinite" : "none"}}></div>
            </div>
            <CopyCode number_code={9} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-blue-500\" style={{'width': '45%', 'backgroundImage': 'repeating-linear-gradient(45deg, rgba(255,255,255,.1) 0px, ...)'}}></div>\n</div>"} />
          </div>

          {/* 10: Pill Shape */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Pill Shape</label>
            <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full" style={{width: "70%"}}></div>
            </div>
            <CopyCode number_code={10} code={"<div className=\"h-3 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-amber-500 rounded-full\" style={{'width': '70%'}}></div>\n</div>"} />
          </div>

          {/* 11: Sharp Edges */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Sharp Edges</label>
            <div className="h-2 bg-slate-700 overflow-hidden">
              <div className="h-full bg-violet-500" style={{width: "55%"}}></div>
            </div>
            <CopyCode number_code={11} code={"<div className=\"h-2 bg-slate-700 overflow-hidden\">\n  <div className=\"h-full bg-violet-500\" style={{'width': '55%'}}></div>\n</div>"} />
          </div>

          {/* 12: With Shadow */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Shadow</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden shadow-lg">
              <div className="h-full bg-amber-500 rounded-full shadow-lg" style={{width: "85%"}}></div>
            </div>
            <CopyCode number_code={12} code={"<div className=\"h-2 bg-slate-700 rounded-full shadow-lg overflow-hidden\">\n  <div className=\"h-full bg-amber-500 rounded-full shadow-lg\" style={{'width': '85%'}}></div>\n</div>"} />
          </div>

          {/* 13: With Glow */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Glow</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-green-400 rounded-full" style={{width: "72%", boxShadow: "0 0 8px rgba(74, 222, 128, 0.6)"}}></div>
            </div>
            <CopyCode number_code={13} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-green-400 rounded-full\" style={{'width': '72%', 'boxShadow': '0 0 8px rgba(74, 222, 128, 0.6)'}}></div>\n</div>"} />
          </div>

          {/* 14: Dual Color */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Dual Color</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden flex">
              <div className="h-full bg-orange-500" style={{width: "40%"}}></div>
              <div className="h-full bg-blue-500" style={{width: "35%"}}></div>
            </div>
            <CopyCode number_code={14} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden flex\">\n  <div className=\"h-full bg-orange-500\" style={{'width': '40%'}}></div>\n  <div className=\"h-full bg-blue-500\" style={{'width': '35%'}}></div>\n</div>"} />
          </div>

          {/* 15: Flex-based Layout */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Flex-based Layout</label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-12">Files:</span>
                <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{width: "88%"}}></div>
                </div>
                <span className="text-xs text-slate-300 w-8">88%</span>
              </div>
            </div>
            <CopyCode number_code={15} code={"<div className=\"flex items-center gap-2\">\n  <span className=\"text-xs text-slate-400 w-12\">Files:</span>\n  <div className=\"flex-1 h-2 bg-slate-700 rounded-full overflow-hidden\">\n    <div className=\"h-full bg-amber-500\" style={{'width': '88%'}}></div>\n  </div>\n  <span className=\"text-xs text-slate-300 w-8\">88%</span>\n</div>"} />
          </div>

          {/* 16: Segmented Progress */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Segmented Progress</label>
            <div className="flex gap-1">
              {[100, 100, 100, 75, 0, 0].map((fill, idx) => (
                <div key={idx} className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-500 rounded-full" style={{width: `${fill}%`}}></div>
                </div>
              ))}
            </div>
            <CopyCode number_code={16} code={"<div className=\"flex gap-1\">\n  {[100, 100, 100, 75, 0, 0].map((fill, idx) => (\n    <div key={'{idx}'} className=\"flex-1 h-2 bg-slate-700 rounded-full overflow-hidden\">\n      <div className=\"h-full bg-teal-500\" style={{'width': '${'{fill}'}%'}}></div>\n    </div>\n  ))}\n</div>"} />
          </div>

          {/* 17: Stacked Progress */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Stacked Progress</label>
            <div className="space-y-2">
              <div className="text-xs text-slate-400 mb-1">Memory:</div>
              <div className="h-3 bg-slate-700 rounded-full overflow-hidden flex">
                <div className="bg-red-500" style={{width: "40%"}}></div>
                <div className="bg-yellow-500" style={{width: "30%"}}></div>
                <div className="bg-green-500" style={{width: "30%"}}></div>
              </div>
            </div>
            <CopyCode number_code={17} code={"<div className=\"h-3 bg-slate-700 rounded-full overflow-hidden flex\">\n  <div className=\"bg-red-500\" style={{'width': '40%'}}></div>\n  <div className=\"bg-yellow-500\" style={{'width': '30%'}}></div>\n  <div className=\"bg-green-500\" style={{'width': '30%'}}></div>\n</div>"} />
          </div>

          {/* 18: Animated Stripes */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Animated Stripes</label>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-amber-500 rounded-full" style={{width: "68%", backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.2) 0px, rgba(255,255,255,.2) 20px, transparent 20px, transparent 40px)", animation: "striped-animation 2s linear infinite"}}></div>
            </div>
            <CopyCode number_code={18} code={"<div className=\"h-2 bg-slate-700 rounded-full overflow-hidden\">\n  <div className=\"h-full bg-gradient-to-r from-amber-500 to-amber-500\" style={{'width': '68%', 'backgroundImage': 'repeating-linear-gradient(...)', 'animation': 'striped-animation 2s infinite'}}></div>\n</div>"} />
          </div>
        </div>
      </div>

      {/* CSS ANIMATIONS */}
      <style jsx>{`
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes striped-animation {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>
    </div>
  );
};

export default ProgressBar;


